// ATTP NEXUS — All 9 module landing pages, built per FSD Part K content.
// Each export is a separate page component using ModuleLandingShell.
import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  Coins, FileText, Wallet, BarChart3, Receipt, Boxes, ShoppingCart, Wrench,
  Building2, Bed, Zap, Droplets, Lightbulb, Shield, Car, Calendar,
  Briefcase, Laptop, Lock,
  GraduationCap, BookOpen, FlaskConical, ChefHat, Stethoscope, Store as StoreIcon, Home,
  Award, QrCode, Users, ShieldCheck,
  Layers, Sparkles, CheckCircle2, Trophy,
  Tv, Plug, Cog, Settings as SettingsIcon, Phone, MapPin, Mail,
  UserPlus, Target, Brain, Clock, ShieldCheck as ShieldIcon, TrendingUp, FileCheck,
} from 'lucide-react';
import ModuleLandingShell, { Hero, Section, FeatureGrid, Steps } from '@/components/layout/ModuleLandingShell';
import { moduleBySlug } from '@/lib/modules';
import { themeFor } from '@/lib/moduleThemes';

/* ============== FINANCIALS LANDING ============== */
export function FinancialsLanding() {
  const m = moduleBySlug('financials'); const t = themeFor('financials');
  const menu = [
    { label: 'Home', href: '#home' }, { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how' }, { label: 'Plans', href: '#subscribe' },
    { label: 'Contact', href: '#subscribe' }, { label: 'Login', href: '/login' },
  ];
  return (
    <ModuleLandingShell module={m} theme={t} menuItems={menu} formSourceTag="Financials Module Request">
      <Hero theme={t} module={m} badge="Finance · Assets · Procurement · Maintenance"
        headline="Complete Financial and Asset Control."
        accentText="One System. No Duplicates."
        sub="Manage your accounts, invoices, budgets, assets, procurement, and maintenance — all connected automatically so you enter information once and the system does the rest."
        ctas={[{ label: 'Subscribe', href: '#subscribe' }, { label: 'See Features', href: '#features' }]}
        imageUrl="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1000&q=80" />

      <Section id="why" theme={t} alt label="Why separate them?" title="Why Separate Your Finance and Asset Systems?"
        sub="Most organisations run their finance in one place, their assets in another, and their procurement somewhere else entirely. ATTP Financials and Asset Management connects all three into one unified system. What happens in procurement reflects in finance. What happens in assets reflects in maintenance. Everything. Automatically.">
      </Section>

      <Section id="features" theme={t} label="Feature Highlights" title="Every finance and asset function — connected">
        <FeatureGrid theme={t} items={[
          { icon: FileText, title: 'Chart of Accounts', desc: 'Structured double-entry accounting.' },
          { icon: Receipt, title: 'Invoices & Receipts', desc: 'Multi-line invoicing with auto-receipt on payment.' },
          { icon: Wallet, title: 'Cash Book & Bank Recon', desc: 'Track inflows/outflows, reconcile statements.' },
          { icon: BarChart3, title: 'Financial Reports', desc: 'P&L · Balance Sheet · Cash Flow · custom ranges.' },
          { icon: Boxes, title: 'Asset Register', desc: 'Single source of truth — feeds every other tool.' },
          { icon: ShoppingCart, title: 'Procurement', desc: 'Requisition → RFQ → PO → Goods Receipt → payment.' },
          { icon: Wrench, title: 'Maintenance', desc: 'Preventive schedules and corrective work orders.' },
          { icon: Sparkles, title: 'AI Budget Builder', desc: 'Sector-specific templates: schools, hospitals, factories, estates.' },
        ]} />
      </Section>

      <Section id="how" theme={t} alt label="How It Works" title="One entry. System-wide update.">
        <Steps theme={t} steps={[
          { title: 'Register an asset', desc: 'Auto-appears in Allocation, Depreciation, Maintenance, Reports.' },
          { title: 'Raise a requisition', desc: 'Moves through approval chain and generates a PO.' },
          { title: 'Create an invoice', desc: 'Download PDF, mark paid, receipt auto-generated.' },
          { title: 'Record maintenance', desc: 'Cost posts to finance accounts without re-entry.' },
        ]} />
      </Section>

      <Section id="for" theme={t} label="Who it is for" title="Built for any organisation that needs financial discipline">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-neutral-700">
          {['Companies and businesses of all sizes', 'Schools and educational institutions',
            'NGOs and not-for-profit organisations', 'Government agencies and parastatals',
            'Estates and facility management organisations'].map((x) => (
              <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.accent }} />{x}</li>
            ))}
        </ul>
      </Section>
    </ModuleLandingShell>
  );
}

/* ============== ESTATE LANDING ============== */
export function EstateLanding() {
  const m = moduleBySlug('estate'); const t = themeFor('estate');
  const menu = [
    { label: 'Home', href: '#home' }, { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how' }, { label: 'Who It Is For', href: '#for' },
    { label: 'Plans', href: '#subscribe' }, { label: 'Login', href: '/login' },
  ];
  return (
    <ModuleLandingShell module={m} theme={t} menuItems={menu} formSourceTag="Estate Module Request">
      <Hero theme={t} module={m} badge="Estate operations · One dashboard"
        headline="Total Estate Operations."
        accentText="One Dashboard."
        sub="Manage housing, power, security, water, vehicles, and all facility operations for your estate — with finance and assets built in from day one."
        ctas={[{ label: 'Subscribe', href: '#subscribe' }, { label: 'See Features', href: '#features' }]}
        imageUrl="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1000&q=80" />
      <Section id="why" theme={t} alt title="Running an Estate Is Complex. Your System Shouldn't Be."
        sub="From housing allocation to generator runtime, from diesel consumption to security incident reports — ATTP for Estate brings every operational function into one connected environment." />
      <Section id="features" theme={t} label="Feature Highlights" title="Every facility, every system, one place">
        <FeatureGrid theme={t} items={[
          { icon: Home, title: 'Housing Register', desc: 'Occupancy tracking, allocation, maintenance requests.' },
          { icon: Bed, title: 'Hostel Management', desc: 'Bed-space allocation, boarding inventory, hostel store.' },
          { icon: Zap, title: 'Power', desc: 'Generators, diesel, petrol, solar — runtime + cost.' },
          { icon: Droplets, title: 'Water', desc: 'Boreholes, tankers, public supply, treatment.' },
          { icon: Lightbulb, title: 'Electricity', desc: 'Meter register, consumption, cost analysis.' },
          { icon: Shield, title: 'Security', desc: 'Personnel, visitor log, CCTV register, incidents.' },
          { icon: Car, title: 'Vehicles', desc: 'Fleet, drivers, fuel, vehicle maintenance.' },
          { icon: Calendar, title: 'Hall & Facility Booking', desc: 'Reservation calendar, equipment tracking.' },
          { icon: Coins, title: 'Embedded Finance & Assets', desc: 'Full Chart of Accounts, invoices, asset register.' },
        ]} />
      </Section>
      <Section id="for" theme={t} alt label="Who it is for" title="Built for residential, institutional and gated communities">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-neutral-700">
          {['Residential estates and gated communities', 'Institutional campuses',
            'Boarding school estates', 'Government housing schemes'].map((x) => (
              <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.accent }} />{x}</li>
            ))}
        </ul>
      </Section>
    </ModuleLandingShell>
  );
}

/* ============== OFFICES LANDING ============== */
export function OfficesLanding() {
  const m = moduleBySlug('offices'); const t = themeFor('offices');
  const menu = [
    { label: 'Home', href: '#home' }, { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how' }, { label: 'Who It Is For', href: '#for' },
    { label: 'Plans', href: '#subscribe' }, { label: 'Login', href: '/login' },
  ];
  return (
    <ModuleLandingShell module={m} theme={t} menuItems={menu} formSourceTag="Offices Module Request">
      <Hero theme={t} module={m} badge="Smart office operations"
        headline="Smart Office Operations."
        accentText="Full Visibility. Zero Chaos."
        sub="From ICT assets to facility bookings, procurement to power management — ATTP for Offices gives your organisation the operational structure it needs to function at its best."
        ctas={[{ label: 'Subscribe', href: '#subscribe' }, { label: 'See Features', href: '#features' }]}
        imageUrl="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1000&q=80" />
      <Section id="why" theme={t} alt title="Your Office Runs on More Than Desks and Wi-Fi."
        sub="Every organisation has assets to track, facilities to manage, people to coordinate, and budgets to control. ATTP for Offices connects all of it." />
      <Section id="features" theme={t} label="Feature Highlights" title="Every tool your office needs">
        <FeatureGrid theme={t} items={[
          { icon: Laptop, title: 'ICT Asset Management', desc: 'Laptops, servers, printers, software licenses.' },
          { icon: Calendar, title: 'Facility Booking', desc: 'Meeting rooms, conference halls, presentation spaces.' },
          { icon: Zap, title: 'Power', desc: 'Generators, UPS, solar, diesel tracking.' },
          { icon: ShoppingCart, title: 'Procurement', desc: 'Full requisition-to-payment workflow.' },
          { icon: Shield, title: 'Security', desc: 'Access control, visitor management, incident reports.' },
          { icon: Users, title: 'Operations', desc: 'Departments, staff assignments, KPI tracking.' },
          { icon: Car, title: 'Vehicles', desc: 'Fleet, drivers, fuel, maintenance.' },
          { icon: Coins, title: 'Embedded Finance & Assets', desc: 'Full finance + asset engine inside.' },
        ]} />
      </Section>
      <Section id="for" theme={t} alt label="Who it is for" title="For professional offices and corporate environments">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-neutral-700">
          {['Corporate offices and professional firms', 'Government offices and agencies',
            'Financial institutions', 'Multi-branch organisations'].map((x) => (
              <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.accent }} />{x}</li>
            ))}
        </ul>
      </Section>
    </ModuleLandingShell>
  );
}

/* ============== EDUCATION LANDING ============== */
export function EducationLanding() {
  const m = moduleBySlug('education'); const t = themeFor('education');
  const menu = [
    { label: 'Home', href: '#home' }, { label: 'Features', href: '#features' },
    { label: 'Fee Management', href: '#fees' }, { label: 'How It Works', href: '#how' },
    { label: 'Plans', href: '#subscribe' }, { label: 'Login', href: '/login' },
  ];
  return (
    <ModuleLandingShell module={m} theme={t} menuItems={menu} formSourceTag="Education Module Request">
      <Hero theme={t} module={m} badge="For schools, by educators"
        headline="Built for Schools."
        accentText="Designed for everything schools need."
        sub="Student fee management, hostel operations, library, laboratory, clinic, kitchen, ICT, security, power — and full finance and asset control. One system. One school."
        ctas={[{ label: 'Subscribe', href: '#subscribe' }, { label: 'See Features', href: '#features' }]}
        imageUrl="https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=1000&q=80" />
      <Section id="why" theme={t} alt title="Schools are complex organisations. They need a system that understands that."
        sub="ATTP for Education is not generic software adapted for schools. It is purpose-built for the operational realities of educational institutions." />
      <Section id="features" theme={t} label="Feature Highlights" title="Finance, fees, facilities — covered.">
        <FeatureGrid theme={t} items={[
          { icon: Coins, title: 'Finance & Fee Mgmt', desc: 'Chart of Accounts · invoices · cash book · reports.' },
          { icon: BookOpen, title: 'Library', desc: 'Cataloguing, borrowing, returns, overdue fines.' },
          { icon: FlaskConical, title: 'Laboratory', desc: 'Equipment register, chemical inventory, calibration.' },
          { icon: ChefHat, title: 'Kitchen', desc: 'Menu planning, food inventory, expiry, cost analysis.' },
          { icon: Stethoscope, title: 'Clinic', desc: 'Medical equipment, drug inventory, dispensing.' },
          { icon: StoreIcon, title: 'Bookshop', desc: 'Books, uniforms, educational materials.' },
          { icon: Bed, title: 'Hostel', desc: 'Bed-space allocation, boarding inventory.' },
          { icon: Laptop, title: 'ICT Management', desc: 'Hardware register, software licenses, helpdesk.' },
          { icon: Shield, title: 'Security & Access', desc: 'Student access control, visitor management.' },
        ]} />
      </Section>
      <Section id="fees" theme={t} alt label="Fee Management" title="How Term Billing Works">
        <Steps theme={t} steps={[
          { title: 'Set up the academic session and terms', desc: 'Define classes, programmes, and intake.' },
          { title: 'Configure fee structure', desc: 'Per class, per term, per programme.' },
          { title: 'Bills generate automatically', desc: 'Every enrolled student receives a bill.' },
          { title: 'Parent receives PDF', desc: 'Downloadable or emailed directly.' },
          { title: 'Record payment', desc: 'Receipt auto-generates, bill clears.' },
          { title: 'Income posts to Finance', desc: 'Auto-posted to school income accounts.' },
        ]} />
      </Section>
    </ModuleLandingShell>
  );
}

/* ============== EVENTS & CERTIFIER LANDING ============== */
export function EventsLanding() {
  const m = moduleBySlug('events'); const t = themeFor('events');
  const menu = [
    { label: 'Home', href: '#home' }, { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how' }, { label: 'Who It Is For', href: '#for' },
    { label: 'Verify Certificate', href: '/verify' }, { label: 'Login', href: '/login' },
  ];
  return (
    <ModuleLandingShell module={m} theme={t} menuItems={menu} formSourceTag="Events & Certifier Request">
      <Hero theme={t} module={m} badge="Smart Events · Secure Access · Verified Certificates"
        headline="Smart Events. Secure Access."
        accentText="Verified Certificates."
        sub="Create events, manage participants, control access with QR codes, and issue tamper-proof certificates automatically — all from one platform."
        ctas={[{ label: 'Subscribe', href: '#subscribe' }, { label: 'Explore Features', href: '#features' }]}
        imageUrl="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1000&q=80" />
      <Section id="features" theme={t} label="Core modules" title="From registration to certificate — fully automated">
        <FeatureGrid theme={t} items={[
          { icon: Calendar, title: 'Event Management', desc: 'Create events, registration links, custom forms, capacity.' },
          { icon: QrCode, title: 'QR Check-in', desc: 'Real-time entry dashboard. Card or code or scan.' },
          { icon: Award, title: 'Certificate Templates', desc: 'Drag-and-edit builder with your logo and branding.' },
          { icon: Sparkles, title: 'Auto + Bulk Issuance', desc: 'Auto-send at event close, or Excel-bulk for 500+ in one click.' },
          { icon: ShieldCheck, title: 'Public Verification', desc: 'Anyone verifies any certificate at the public portal.' },
          { icon: Users, title: 'Access & Identity', desc: 'Premises access, dependent linking, movement logs.' },
        ]} />
      </Section>
      <Section id="how" theme={t} alt label="How It Works" title="Five steps. Done.">
        <Steps theme={t} steps={[
          { title: 'Set up your organisation' }, { title: 'Create your event' },
          { title: 'Share registration link' }, { title: 'Verify entries on event day' },
          { title: 'Close event → certificates issued automatically' },
        ]} />
      </Section>
      <Section id="for" theme={t} label="Who it is for" title="One platform, many use cases">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-neutral-700">
          {['Schools — graduation certificates · parent access', 'Corporates — conferences · training programmes',
            'Estates — resident identity · gate access', 'Government & NGO — verifiable participation certificates',
            'Examination bodies — fraud-proof credentialing', 'Seminars and workshops'].map((x) => (
              <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.accent }} />{x}</li>
            ))}
        </ul>
      </Section>
    </ModuleLandingShell>
  );
}

/* ============== PATHWAY CLUB (LMS) LANDING ============== */
export function LmsLanding() {
  const m = moduleBySlug('lms'); const t = themeFor('lms');
  const menu = [
    { label: 'Home', href: '#home' }, { label: 'How It Works', href: '#how' },
    { label: 'Courses', href: '#courses' }, { label: 'Who It Is For', href: '#for' },
    { label: 'Plans', href: '#subscribe' }, { label: 'Login', href: '/login' },
  ];
  return (
    <ModuleLandingShell module={m} theme={t} menuItems={menu} formSourceTag="Pathway Club Enrolment">
      <Hero theme={t} module={m} badge="Pathway Club · Mastery learning"
        headline="Learn. Progress. Certify."
        accentText="No Shortcuts."
        sub="A structured digital learning system built on mastery. Move from Foundation to Specialist — one level at a time, one domain at a time — and earn credentials that actually mean something."
        ctas={[{ label: 'Enrol Now', href: '#subscribe' }, { label: 'See How It Works', href: '#how' }]}
        imageUrl="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1000&q=80" />
      <Section id="why" theme={t} alt title="Competency First. Certification Second."
        sub="ATTP Pathway Club does not issue certificates because you paid for a course. It issues them because you passed. Every level is locked until you prove mastery at the one before it. Lessons. Tasks. Quizzes. Badges. That is the pathway." />
      <Section id="how" theme={t} label="Learning structure" title="Four levels. Five domains. Strict progression.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-5 bg-white border rounded-md">
            <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: t.accent }}>The Four Levels</div>
            <ol className="space-y-2 text-sm">
              <li className="flex items-center gap-2"><Trophy className="w-4 h-4" style={{ color: t.accent }} /> Foundation — open to all enrolled learners</li>
              <li className="flex items-center gap-2"><Lock className="w-4 h-4 text-neutral-400" /> Intermediate — unlocks after Foundation</li>
              <li className="flex items-center gap-2"><Lock className="w-4 h-4 text-neutral-400" /> Professional — unlocks after Intermediate</li>
              <li className="flex items-center gap-2"><Lock className="w-4 h-4 text-neutral-400" /> Specialist — unlocks after Professional</li>
            </ol>
          </div>
          <div className="p-5 bg-white border rounded-md">
            <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: t.accent }}>The Five Domains per level</div>
            <ul className="space-y-1.5 text-sm text-neutral-700">
              {['Business, ICT and Entrepreneurship', 'Coding and Programming', 'Hardware and Repair', 'Internet and Networking', 'Electrical Installation']
                .map((d) => <li key={d} className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: t.accent }} />{d}</li>)}
            </ul>
          </div>
        </div>
      </Section>
      <Section id="courses" theme={t} alt label="Domain flow" title="Lesson Notes → Tasks → Assignments → Quiz → Badge">
        <Steps theme={t} steps={[
          { title: 'Lesson Notes', desc: 'Step-by-step structured content with examples.' },
          { title: 'Practical Tasks', desc: 'Hands-on simulation and applied exercises.' },
          { title: 'Assignments', desc: 'Submitted long-form work where required.' },
          { title: 'Quiz', desc: '100-MCQ assessment. Pass to unlock the next.' },
        ]} />
      </Section>
      <Section id="for" theme={t} label="Who it is for" title="For learners and institutions">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-neutral-700">
          {['Individuals seeking structured technology skills', 'Schools and training institutions running ICT programmes',
            'Organisations building internal technical capacity', 'Professionals pursuing certification in digital and electrical disciplines']
            .map((x) => <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.accent }} />{x}</li>)}
        </ul>
      </Section>
    </ModuleLandingShell>
  );
}

/* ============== HEALTHCARE LANDING (NEW additional module) ============== */
export function HealthcareLanding() {
  const m = moduleBySlug('healthcare'); const t = themeFor('healthcare');
  const menu = [
    { label: 'Home', href: '#home' }, { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how' }, { label: 'Who It Is For', href: '#for' },
    { label: 'Plans', href: '#subscribe' }, { label: 'Login', href: '/login' },
  ];
  return (
    <ModuleLandingShell module={m} theme={t} menuItems={menu} formSourceTag="Healthcare Module Request">
      <Hero theme={t} module={m} badge="Hospital · Clinic · Pharmacy"
        headline="Hospital, clinic and pharmacy operations."
        accentText="In one connected system."
        sub="EMR · OPD queue · vitals · lab orders · ward management · pharmacy stock · billing & HMO. Designed for hospitals, clinics and dispensaries running their own pharmacy store."
        ctas={[{ label: 'Subscribe', href: '#subscribe' }, { label: 'See Features', href: '#features' }]}
        imageUrl="https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=1000&q=80" />
      <Section id="features" theme={t} label="Feature Highlights" title="From the consult to the dispense">
        <FeatureGrid theme={t} items={[
          { icon: Stethoscope, title: 'EMR & Patient Records', desc: 'Full clinical records with history.' },
          { icon: Users, title: 'OPD Queue', desc: 'Live consult queue and triage.' },
          { icon: BarChart3, title: 'Vitals Flowsheet', desc: 'BP, temp, pulse, SpO₂ trends.' },
          { icon: FlaskConical, title: 'Lab Orders', desc: 'Order → specimen → result → report.' },
          { icon: StoreIcon, title: 'Pharmacy Store', desc: 'Drug inventory, dispense, expiry alerts.' },
          { icon: Bed, title: 'Ward & Beds', desc: 'Admissions, bed allocation, rounds.' },
          { icon: Receipt, title: 'Billing & HMO', desc: 'Auto-billing with HMO claims.' },
          { icon: Shield, title: 'Theatre / OT', desc: 'Scheduling and checklists.' },
          { icon: Coins, title: 'Embedded Finance', desc: 'Full finance + asset engine inside.' },
        ]} />
      </Section>
      <Section id="for" theme={t} alt label="Who it is for" title="For healthcare providers of every size">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-neutral-700">
          {['Hospitals (private and public)', 'Clinics and diagnostic centres',
            'Pharmacies and dispensaries', 'Medical centres with their own store'].map((x) => (
              <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.accent }} />{x}</li>
            ))}
        </ul>
      </Section>
    </ModuleLandingShell>
  );
}

/* ============== ENGINEERING LANDING (public site) ============== */
export function EngineeringLandingV2() {
  const m = moduleBySlug('engineering'); const t = themeFor('engineering');
  const menu = [
    { label: 'Home', href: '#home' }, { label: 'Services', href: '#services' },
    { label: 'Process', href: '#process' }, { label: 'Contact Us', href: '#subscribe' },
    { label: 'Request Service', href: '#subscribe' }, { label: 'Login', href: '/login' },
  ];
  return (
    <ModuleLandingShell module={m} theme={t} menuItems={menu} formSourceTag="Engineering Service Request">
      <Hero theme={t} module={m} badge="CAC Registered · Government Approved"
        headline="Your Trusted Engineering"
        accentText="Partner."
        sub="ATTP Engineering — Olu-Akintade Computer and Electrical Works Engineering. Professional electrical installation, computer repairs, electronics servicing, and technical solutions with the reliability and expertise you can count on."
        ctas={[{ label: 'Request a Service', href: '#subscribe' }, { label: 'View Our Services', href: '#services' }]}
        imageUrl="https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=1000&q=80" />
      <div className="bg-white py-4 border-y" style={{ borderColor: t.accent }}>
        <div className="container-x flex items-center justify-center gap-8 text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: t.primary }}>
          <span>Electrical</span><span style={{ color: t.accent }}>·</span>
          <span>Computers</span><span style={{ color: t.accent }}>·</span>
          <span>Electronics</span>
        </div>
      </div>
      <Section id="mission" theme={t} alt label="Mission · Vision · Values" title="Built on trust. Driven by excellence.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-5 bg-white border rounded-md">
            <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: t.accent }}>Mission</div>
            <p className="text-sm text-neutral-700 leading-relaxed">To deliver reliable, efficient, and high-quality engineering solutions that meet the needs of individuals, businesses, and industries.</p>
          </div>
          <div className="p-5 bg-white border rounded-md">
            <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: t.accent }}>Vision</div>
            <p className="text-sm text-neutral-700 leading-relaxed">To become a leading engineering service provider known for excellence, innovation, and trust.</p>
          </div>
          <div className="p-5 bg-white border rounded-md">
            <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: t.accent }}>Core Values</div>
            <ul className="text-sm text-neutral-700 space-y-1">
              <li>Integrity</li><li>Professionalism</li><li>Excellence</li><li>Reliability</li><li>Customer Satisfaction</li>
            </ul>
          </div>
        </div>
      </Section>
      <Section id="services" theme={t} label="Our Services" title="Nine service categories, one trusted team.">
        <FeatureGrid theme={t} items={[
          { icon: Laptop, title: 'Computer Repairs', desc: 'Laptop screens, motherboards, OS installation, data recovery.' },
          { icon: Plug, title: 'Electrical Installation', desc: 'Wiring, panel upgrades, generator connection, solar.' },
          { icon: Tv, title: 'Electronics Repairs', desc: 'TVs, sound systems, appliances, component-level fixes.' },
          { icon: StoreIcon, title: 'Sales & Supply', desc: 'Laptops, electrical materials, accessories, bulk supply.' },
          { icon: Wrench, title: 'Maintenance Contracts', desc: 'Monthly inspections, priority response, discounted rates.' },
          { icon: Shield, title: 'CCTV & Security', desc: 'CCTV install, IP cameras, DVR/NVR, remote monitoring.' },
          { icon: Cog, title: 'Network & IT Setup', desc: 'Cabling, WiFi deployment, router configuration.' },
          { icon: SettingsIcon, title: 'Industrial Equipment', desc: 'Generators, UPS, industrial wiring, machine repairs.' },
          { icon: Sparkles, title: 'Additional Services', desc: 'Audits, fit-out, smart home/office, solar inverter.' },
        ]} />
      </Section>
      <Section id="process" theme={t} alt label="Our Process" title="Simple. Transparent. Accountable.">
        <Steps theme={t} steps={[
          { title: 'Submit request', desc: 'Service type, location, description.' },
          { title: 'Response within 24h', desc: 'Estimate range provided.' },
          { title: 'Approve & pay 80% upfront', desc: 'Engineer is deployed.' },
          { title: 'Live tracking', desc: 'Arrival time and progress visible.' },
          { title: 'Job completion', desc: 'Tax-compliant report + 90-day warranty.' },
          { title: 'Pay remaining 20%', desc: 'On completion.' },
        ]} />
      </Section>
    </ModuleLandingShell>
  );
}

/* ============== CORPORATE HUB LANDING (public site) ============== */
export function CorporateLanding() {
  const m = moduleBySlug('corporate'); const t = themeFor('corporate');
  const menu = [
    { label: 'Home', href: '#home' },
    { label: 'About', dropdown: [
      { label: 'About ATTP Corporate Hub', href: '#about' },
      { label: 'Our Core Values', href: '#values' },
      { label: 'Lead Consultant', href: '#consultant' },
    ]},
    { label: 'Services', href: '#services' }, { label: 'Why Us', href: '#why' },
    { label: 'Contact', href: '#subscribe' }, { label: 'Get Started', href: '#subscribe' },
  ];
  return (
    <ModuleLandingShell module={m} theme={t} menuItems={menu} formSourceTag="Corporate Hub Consultation">
      <Hero theme={t} module={m} badge="Active across 13+ African countries"
        headline="Building Stronger"
        accentText="African Institutions."
        sub="A professional consulting, training, and education practice helping African organisations build structured systems, develop capable leaders, and achieve sustainable institutional growth."
        ctas={[{ label: 'Request a Consultation', href: '#subscribe' }, { label: 'View Our Services', href: '#services' }]}
        imageUrl="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1000&q=80" />

      <div className="bg-white py-5 border-b" style={{ borderColor: `${t.accent}40` }}>
        <div className="container-x flex flex-wrap items-center justify-around gap-6 text-center">
          <div><div className="font-display font-bold text-2xl" style={{ color: t.accent }}>13+</div><div className="text-xs uppercase tracking-wider text-neutral-500">African countries</div></div>
          <div><div className="font-display font-bold text-2xl" style={{ color: t.accent }}>100+</div><div className="text-xs uppercase tracking-wider text-neutral-500">Organisations served</div></div>
          <div><div className="font-display font-bold text-2xl" style={{ color: t.accent }}>20+</div><div className="text-xs uppercase tracking-wider text-neutral-500">Years of experience</div></div>
        </div>
      </div>

      <Section id="about" theme={t} label="About" title="A practice built for African institutions"
        sub="ATTP Corporate Hub is a professional consulting, training, and education practice dedicated to helping organisations build structured systems, develop leadership capacity, and achieve sustainable institutional growth. Our approach is practical, grounded in real institutional experience, and deeply informed by African contextual realities." />

      <Section id="values" theme={t} alt label="Vision · Mission · Values" title="What we stand for">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-5 bg-white border rounded-md">
            <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: t.accent }}>Vision</div>
            <p className="text-sm text-neutral-700 leading-relaxed">To strengthen African institutions through structured systems, capable leadership, and sustainable growth.</p>
          </div>
          <div className="p-5 bg-white border rounded-md">
            <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: t.accent }}>Mission</div>
            <p className="text-sm text-neutral-700 leading-relaxed">To deliver practical, high-impact consulting, training, and education solutions tailored to African realities.</p>
          </div>
          <div className="p-5 bg-white border rounded-md">
            <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: t.accent }}>Core Values</div>
            <ul className="text-sm text-neutral-700 space-y-1">
              <li>Structure</li><li>Excellence</li><li>Practicality</li><li>Integrity</li><li>Impact</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section id="services" theme={t} label="Services" title="Three pillars. One transformation practice.">
        <FeatureGrid theme={t} items={[
          { icon: Briefcase, title: 'Business Consulting', desc: 'Org structure, operations, HR systems, procurement, asset control, policy development, financial discipline.' },
          { icon: Users, title: 'Corporate Training', desc: 'Leadership, administrative excellence, HR compliance, customer service, operational efficiency.' },
          { icon: GraduationCap, title: 'Professional Education', desc: 'School administration, boarding systems, staff development, compliance, growth planning, ICT integration.' },
        ]} />
      </Section>

      <Section id="why" theme={t} alt label="Why us" title="We do not just deliver services. We drive transformation.">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-neutral-700">
          {['Pan-African reach — active across 13+ countries', 'Global standard delivery — internationally benchmarked',
            'Expert facilitators — deep institutional experience', 'Certified programmes — credentialled and verifiable',
            'Flexible delivery — physical, virtual, blended', 'Dedicated support — pre, during, post-engagement',
            'Measurable outcomes — defined deliverables', 'Customised solutions — no generic programmes',
            'Post-program support — we stay engaged'].map((x) => (
              <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.accent }} />{x}</li>
            ))}
        </ul>
      </Section>

      <Section id="consultant" theme={t} label="Lead Consultant" title="Olusegun Akintade, MA, PGD, BEng, BSc">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-3 text-sm text-neutral-700 leading-relaxed">
            <div className="font-display font-semibold" style={{ color: t.accent }}>Lead Consultant and Founder — ATTP Corporate Hub</div>
            <p>Olusegun Akintade is a seasoned institutional development expert with extensive experience in education management, human resource systems, ICT integration, organisational restructuring, and operational systems design. He has worked across 13+ African countries, supporting institutions in building structured, scalable, and performance-driven systems.</p>
            <p className="italic" style={{ color: t.primary }}>&ldquo;Sustainable institutional success is achieved through structured processes, strong governance, and disciplined execution.&rdquo;</p>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: t.accent }}>Core expertise</div>
              <ul className="space-y-1">
                <li>• Organisational Consulting — structure, HR, procurement, governance</li>
                <li>• Education Systems Development — schools, boarding, compliance</li>
                <li>• Corporate Training — leadership, administration, operations</li>
                <li>• Digital Transformation — ERP, automation, reporting</li>
              </ul>
            </div>
          </div>
          <div className="p-5 rounded-md" style={{ background: t.primary, color: t.textOnPrimary }}>
            <div className="text-xs uppercase tracking-wider mb-3" style={{ color: t.accent }}>Direct contact</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 flex-shrink-0" /> oa@attpnexus.com.ng</div>
              <div className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 flex-shrink-0" /> +234 (803) 517 5136</div>
              <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /> Nigeria, West Africa</div>
            </div>
          </div>
        </div>
      </Section>
    </ModuleLandingShell>
  );
}

/* ============== HR LANDING (Enterprise HCM) ============== */
export function HrLanding() {
  const m = moduleBySlug('hr'); const t = themeFor('hr');
  const menu = [
    { label: 'Home', href: '#home' }, { label: 'Capabilities', href: '#capabilities' },
    { label: 'Lifecycle', href: '#lifecycle' }, { label: 'Intelligence', href: '#ai' },
    { label: 'Plans', href: '#subscribe' }, { label: 'Login', href: '/login' },
  ];
  return (
    <ModuleLandingShell module={m} theme={t} menuItems={menu} formSourceTag="ATTP-HR Enterprise HCM Request">
      <Hero theme={t} module={m} badge="Enterprise Human Capital Management"
        headline="The Single Source of Truth"
        accentText="for Your Workforce."
        sub="Master Data Architecture · workflow-driven approvals · biometric attendance · AI workforce intelligence · executive command centre. Built for organisations from 50 to 100,000+ employees — no architectural redesign."
        ctas={[{ label: 'Subscribe', href: '#subscribe' }, { label: 'See Capabilities', href: '#capabilities' }]}
        imageUrl="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1000&q=80" />

      <Section id="why" theme={t} alt title="Create Once. Use Everywhere."
        sub="Every employee exists only once in the database. The Employee Master Record is the foundation. Recruitment, payroll, leave, attendance, performance, learning, benefits, assets, medical, compliance, succession and exit all draw from the same record. Zero duplicate entry. Zero data drift." />

      <Section id="capabilities" theme={t} label="Functional Domains" title="12 functional domains. One unified platform.">
        <FeatureGrid theme={t} items={[
          { icon: Users, title: 'Core HR', desc: 'Employee admin · records · movement · org chart.' },
          { icon: UserPlus, title: 'Recruitment & ATS', desc: 'Vacancies · candidate portal · talent pools · interviews.' },
          { icon: FileCheck, title: 'Onboarding', desc: 'Documentation · orientation · asset & access provisioning.' },
          { icon: Clock, title: 'Attendance', desc: 'Biometric · facial · GPS · shifts · overtime.' },
          { icon: Calendar, title: 'Leave Management', desc: 'Requests · approvals · balances · accrual rules.' },
          { icon: Wallet, title: 'Payroll', desc: 'Tax · pension · statutory compliance · multi-country.' },
          { icon: Award, title: 'Benefits', desc: 'Health · housing · education · transport.' },
          { icon: Target, title: 'Performance', desc: 'KPIs · OKRs · competency · 360° feedback.' },
          { icon: BookOpen, title: 'Learning & Development', desc: 'LMS · e-learning · certification tracking.' },
          { icon: TrendingUp, title: 'Talent & Succession', desc: 'Talent reviews · high-potential ID · leadership pipeline.' },
          { icon: ShieldIcon, title: 'Compliance', desc: 'Labour law · tax · pension · immigration · OSH.' },
          { icon: Boxes, title: 'Asset Management', desc: 'Allocation · returns · recovery linked to employee.' },
        ]} />
      </Section>

      <Section id="lifecycle" theme={t} alt label="Employee Lifecycle" title="Nine stages. One continuous journey.">
        <Steps theme={t} steps={[
          { title: 'Workforce Planning', desc: 'Establishment · position · headcount · budget.' },
          { title: 'Recruitment', desc: 'Vacancies · candidate tracking · assessments.' },
          { title: 'Selection', desc: 'Evaluation · scoring · ranking · approval.' },
          { title: 'Hiring', desc: 'Offer · contract · employment approval.' },
          { title: 'Onboarding', desc: 'Docs · orientation · asset & access setup.' },
          { title: 'Active Employment', desc: 'Attendance · leave · payroll · performance.' },
          { title: 'Growth', desc: 'Promotion · transfer · succession · leadership.' },
          { title: 'Separation', desc: 'Resignation · retirement · termination · clearance.' },
          { title: 'Alumni', desc: 'Former employee database · rehire eligibility.' },
        ]} />
      </Section>

      <Section id="ai" theme={t} label="Intelligence Layer" title="AI workforce intelligence — built in.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { title: 'Attrition Prediction', desc: 'Identify flight risk before it costs you a top performer.' },
            { title: 'Promotion Readiness', desc: 'AI-scored readiness on performance, skills, and tenure signals.' },
            { title: 'Workforce Forecasting', desc: 'Headcount, payroll cost and skills demand projection.' },
            { title: 'Succession Risk Analysis', desc: 'Critical-role coverage and successor readiness dashboard.' },
            { title: 'Diversity & Inclusion Analytics', desc: 'Headcount, pay equity, leadership representation tracking.' },
            { title: 'Smart Search & Voice', desc: 'System-wide natural-language search across employees, policies and documents.' },
          ].map((x) => (
            <div key={x.title} className="p-5 rounded-md" style={{ background: t.primary, color: t.textOnPrimary }}>
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-5 h-5" style={{ color: t.accent }} />
                <div className="font-display font-semibold">{x.title}</div>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>{x.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="dashboards" theme={t} alt label="Executive Command Centre" title="Six role-based dashboards. Always on.">
        <FeatureGrid theme={t} items={[
          { icon: Target, title: 'CEO Dashboard', desc: 'Workforce cost · productivity · leadership bench strength.' },
          { icon: Users, title: 'CHRO Dashboard', desc: 'Headcount · pipeline · turnover risk · compliance.' },
          { icon: Briefcase, title: 'Department Head', desc: 'Team attendance · leave · KPI · training needs.' },
          { icon: Users, title: 'Manager', desc: 'Pending approvals · team performance · succession.' },
          { icon: BookOpen, title: 'Employee', desc: 'Profile · payslips · leave balance · goals · benefits.' },
          { icon: ShieldIcon, title: 'Compliance Officer', desc: 'Mandatory training · regulatory · audit management.' },
        ]} />
      </Section>

      <Section id="for" theme={t} label="Who it is for" title="Built for organisations of every shape and scale">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-neutral-700">
          {['Schools and universities', 'Hospitals and healthcare systems',
            'Manufacturing and factories', 'Banks and financial services',
            'Government agencies and parastatals', 'NGOs and development agencies',
            'Conglomerates and holding groups', 'Multi-country and multi-entity organisations']
            .map((x) => <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.accent }} />{x}</li>)}
        </ul>
      </Section>
    </ModuleLandingShell>
  );
}

/* ============== ROUTER — picks the right landing based on slug ============== */
export default function ModuleLandingRouter() {
  const { slug } = useParams();
  const map = {
    financials: FinancialsLanding, estate: EstateLanding, offices: OfficesLanding,
    education: EducationLanding, events: EventsLanding, lms: LmsLanding,
    healthcare: HealthcareLanding, hr: HrLanding,
    engineering: EngineeringLandingV2, corporate: CorporateLanding,
  };
  const Component = map[slug];
  if (!Component) return <Navigate to="/" replace />;
  return <Component />;
}
